// The screen's way to the server's REST routes (/api/me/...).
//
// Promise:
// - Same origin, JSON in and out. Every request says which language the screen
//   shows and which time zone the browser is in (the server remembers both for
//   what it writes outside the screen).
// - A non-2xx answer becomes an ApiError with the server's code, message and
//   details (`{ error: { code, message, details } }`; a bare `{ error: "code" }`
//   is read too). 401 also calls the host's `onUnauthorized` (cloud sends the
//   person to sign in; the offline edition has no sign-in).
// - `idempotencyKey` is sent as Idempotency-Key. `idempotencyKeyFor(input)`
//   gives one key per mutation call, the same for its retries.
import { getLocale } from "@trameria/i18n";

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiInit extends Omit<RequestInit, "body"> {
  /** A JSON body (serialized here) or a ready body (FormData, Blob). */
  body?: unknown;
  idempotencyKey?: string;
}

export type Api = <T>(path: string, init?: ApiInit) => Promise<T>;

export interface ApiOptions {
  /** Prefix of every path ("" = the screen's own origin). */
  base?: string;
  fetch?: typeof fetch;
  onUnauthorized?: () => void;
}

export const LOCALE_HEADER = "x-trameria-locale";
export const TIME_ZONE_HEADER = "x-trameria-time-zone";

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

const isRawBody = (body: unknown) =>
  typeof body === "string" ||
  body instanceof FormData ||
  body instanceof Blob ||
  body instanceof ArrayBuffer ||
  body instanceof URLSearchParams;

export function createApi(options: ApiOptions = {}): Api {
  const base = options.base ?? "";
  return async <T>(path: string, init: ApiInit = {}): Promise<T> => {
    const { body, idempotencyKey, headers, ...rest } = init;
    const json = body !== undefined && !isRawBody(body);
    const doFetch = options.fetch ?? globalThis.fetch;
    const res = await doFetch(`${base}${path}`, {
      ...rest,
      credentials: "include",
      headers: {
        ...(json ? { "Content-Type": "application/json" } : {}),
        [LOCALE_HEADER]: getLocale(),
        [TIME_ZONE_HEADER]: browserTimeZone(),
        ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
        ...headers,
      },
      body: json ? JSON.stringify(body) : (body as BodyInit | undefined),
    });
    const text = await res.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = null;
    }
    if (!res.ok) {
      const error = (parsed as { error?: unknown } | null)?.error;
      const detail =
        typeof error === "string"
          ? { code: error }
          : ((error ?? {}) as {
              code?: string;
              message?: string;
              details?: unknown;
            });
      if (res.status === 401) options.onUnauthorized?.();
      throw new ApiError(
        detail.code ?? "unknown",
        detail.message ?? res.statusText,
        res.status,
        detail.details,
      );
    }
    return parsed as T;
  };
}

const keys = new WeakMap<object, string>();

/**
 * One Idempotency-Key per mutation call. TanStack Query retries a mutation with
 * the same variables object, so keying on that object gives retries the same
 * key and a new call (a new object) a new one.
 */
export function idempotencyKeyFor(input: object): string {
  let key = keys.get(input);
  if (!key) {
    key = crypto.randomUUID();
    keys.set(input, key);
  }
  return key;
}
